// Public ticket tracking page - linked from SMS replies
// URL: /track/:ticketNumber
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, Clock, Search, Phone, Mail, MapPin, User, Calendar, Loader2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_FLOW = [
  { key: 'pending', label: 'Received', desc: 'Your request was received' },
  { key: 'assigned', label: 'Technician Assigned', desc: 'A technician has been assigned' },
  { key: 'in_progress', label: 'In Progress', desc: 'Work is underway' },
  { key: 'completed', label: 'Completed', desc: 'Work is done' },
];

const statusIndex = (s) => {
  const i = STATUS_FLOW.findIndex(x => x.key === s);
  return i === -1 ? 0 : i;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const TicketTracker = () => {
  const { ticket } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/public/ticket/${ticket}`);
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || `Ticket not found (${res.status})`);
        }
        const j = await res.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [ticket]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="ticket-loading">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="ticket-error">
        <div className="max-w-md w-full bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
          <Search className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Ticket Not Found</h1>
          <p className="text-sm text-gray-400 mb-6">{error}</p>
          <p className="text-xs text-gray-500 mb-4">Ticket reference: <span className="text-white font-mono">{ticket}</span></p>
          <Link to="/" className="inline-block bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded text-sm font-medium transition-colors">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const idx = statusIndex(data.status);
  const isCompleted = data.status === 'completed';

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ticket-tracker">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-950">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded flex items-center justify-center font-black text-white">B</div>
            <div>
              <p className="font-bold tracking-wider">BELLAJA</p>
              <p className="text-[10px] text-gray-500 tracking-widest">ELECTRICAL & SECURITY</p>
            </div>
          </Link>
          <a href="tel:+12038037035" className="hidden sm:flex items-center gap-2 text-sm text-gray-400 hover:text-white">
            <Phone className="w-4 h-4" />
            <span className="font-mono">(203) 803-7035</span>
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* Ticket header */}
        <div className="mb-8">
          <p className="text-xs text-red-500 font-bold tracking-[0.3em] mb-2">SERVICE REQUEST</p>
          <h1 className="text-3xl font-bold mb-1" data-testid="ticket-number">{data.ticket_number}</h1>
          <p className="text-sm text-gray-500">
            Created {fmtDate(data.created_at)}
            {data.customer_name ? <> &middot; for {data.customer_name}</> : null}
          </p>
        </div>

        {/* Status timeline */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
          <p className="text-[10px] text-gray-500 font-bold tracking-widest mb-5">PROGRESS</p>
          <div className="space-y-5">
            {STATUS_FLOW.map((step, i) => {
              const active = i <= idx;
              const current = i === idx && !isCompleted;
              return (
                <div key={step.key} className="flex items-start gap-4" data-testid={`status-step-${step.key}`}>
                  <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                    active ? (current ? 'bg-red-600 border-red-500 animate-pulse' : 'bg-green-600 border-green-500') : 'bg-gray-800 border-gray-700'
                  }`}>
                    {active && !current ? (
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    ) : current ? (
                      <Clock className="w-4 h-4 text-white" />
                    ) : (
                      <span className="text-xs text-gray-500 font-bold">{i + 1}</span>
                    )}
                  </div>
                  <div className="flex-1 pb-1">
                    <p className={`font-semibold ${active ? 'text-white' : 'text-gray-500'}`}>{step.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {data.service_type && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4" data-testid="ticket-service-type">
              <p className="text-[10px] text-gray-500 font-bold tracking-widest mb-1">SERVICE</p>
              <p className="text-sm text-white capitalize">{String(data.service_type).replace(/_/g, ' ')}</p>
            </div>
          )}
          {data.priority && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <p className="text-[10px] text-gray-500 font-bold tracking-widest mb-1">PRIORITY</p>
              <p className="text-sm text-white capitalize">{data.priority}</p>
            </div>
          )}
          {data.assigned_technician_name && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 sm:col-span-2" data-testid="ticket-technician">
              <p className="text-[10px] text-gray-500 font-bold tracking-widest mb-1 flex items-center gap-1.5"><User className="w-3 h-3" />ASSIGNED TECHNICIAN</p>
              <p className="text-sm text-white">{data.assigned_technician_name}</p>
            </div>
          )}
          {data.address && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 sm:col-span-2">
              <p className="text-[10px] text-gray-500 font-bold tracking-widest mb-1 flex items-center gap-1.5"><MapPin className="w-3 h-3" />ADDRESS</p>
              <p className="text-sm text-white">{data.address}</p>
            </div>
          )}
          {data.scheduled_date && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 sm:col-span-2">
              <p className="text-[10px] text-gray-500 font-bold tracking-widest mb-1 flex items-center gap-1.5"><Calendar className="w-3 h-3" />SCHEDULED</p>
              <p className="text-sm text-white">{fmtDate(data.scheduled_date)}</p>
            </div>
          )}
          {data.description && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 sm:col-span-2">
              <p className="text-[10px] text-gray-500 font-bold tracking-widest mb-1">DESCRIPTION</p>
              <p className="text-sm text-gray-300 whitespace-pre-wrap">{data.description}</p>
            </div>
          )}
        </div>

        {/* Contact card */}
        <div className="bg-gradient-to-br from-red-900/20 to-gray-900 border border-red-900/40 rounded-xl p-6">
          <p className="text-xs text-red-400 font-bold tracking-[0.3em] mb-3">QUESTIONS?</p>
          <p className="text-sm text-gray-300 mb-4">Our team is here to help. Reach us anytime:</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="tel:+12038037035" className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded font-medium transition-colors">
              <Phone className="w-4 h-4" /> Call (203) 803-7035
            </a>
            <a href="mailto:info@bellajaelectric.com" className="flex-1 flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-3 rounded font-medium transition-colors">
              <Mail className="w-4 h-4" /> Email Us
            </a>
          </div>
        </div>

        <p className="text-center text-xs text-gray-600 mt-8">
          NYC DOB Licensed &middot; CT State Licensed &middot; Fully Insured & Bonded
        </p>
      </main>
    </div>
  );
};

export default TicketTracker;
